/**
 * This file contains a collection of utils that modify a hot instance directly and also
 * use the "session" object to schedule a flush, etc.
 *
 * As such they work on the active hot table only.
 */

import Handsontable from 'handsontable';
import {
    applyHotTableTextStyle,
    CellDataChangeEntry,
    CellDataUpdateFunction,
    setHotTableCellData,
} from './hotTableUpdateUtils';
import { CellData, CellTypeObject, TableChangeSource } from '../../../../../common/table/TableTypes';
import { TextAlignment, VerticalAlignment } from '../../../../../common/table/CellTypeConstants';
import { getAllCellsBetween } from '../tableCellSelectionUtils';
import { ActiveTableSession } from '../../store/hotTableStoreTypes';

type ActiveHotTableMutationArgs = {
    hotInstance: Handsontable;
    session: ActiveTableSession;
};

/**
 * Applies cell-data changes to the active HOT instance and lets the session debounce persistence.
 */
export const updateActiveHotTableEditedData = ({
    hotInstance,
    session,
    cellDataChanges,
    tableUpdateSource,
}: ActiveHotTableMutationArgs & {
    cellDataChanges: CellDataChangeEntry[];
    tableUpdateSource: TableChangeSource;
}): void => {
    setHotTableCellData(hotInstance, cellDataChanges, tableUpdateSource);
    session.markDirty(tableUpdateSource);
};

/**
 * Applies the provided cell-data transform across the active HOT selection.
 */
export const updateActiveHotTableSelectionData = ({
    hotInstance,
    session,
    cellDataUpdateFn,
    tableUpdateSource,
}: ActiveHotTableMutationArgs & {
    cellDataUpdateFn: CellDataUpdateFunction;
    tableUpdateSource: TableChangeSource;
}): void => {
    const cellSelections = hotInstance?.getSelected();
    const cellDataChanges = getAllCellsBetween(cellSelections).map<[number, number, (cellData: CellData) => CellData]>(
        ({ row, col }) => [row, col, cellDataUpdateFn],
    );

    updateActiveHotTableEditedData({
        hotInstance,
        session,
        cellDataChanges,
        tableUpdateSource,
    });
};

/**
 * Updates the text alignment for the selected cells in the active HOT instance.
 */
export const updateActiveHotTableCellTextAlignment = ({
    hotInstance,
    session,
    textAlignment,
}: ActiveHotTableMutationArgs & {
    textAlignment: TextAlignment;
}): void =>
    updateActiveHotTableSelectionData({
        hotInstance,
        session,
        cellDataUpdateFn: (cellData: CellData) => ({
            ...cellData,
            textAlignment,
        }),
        tableUpdateSource: TableChangeSource.CELL_META_UPDATE_CELL_TEXT_ALIGNMENT,
    });

/**
 * Updates the vertical alignment for the selected cells in the active HOT instance.
 */
export const updateActiveHotTableCellVerticalAlignment = ({
    hotInstance,
    session,
    verticalAlignment,
}: ActiveHotTableMutationArgs & {
    verticalAlignment: VerticalAlignment;
}): void =>
    updateActiveHotTableSelectionData({
        hotInstance,
        session,
        cellDataUpdateFn: (cellData: CellData) => ({
            ...cellData,
            verticalAlignment,
        }),
        tableUpdateSource: TableChangeSource.CELL_META_UPDATE_CELL_VERTICAL_ALIGNMENT,
    });

/**
 * Updates the background colour for the selected cells in the active HOT instance.
 */
export const updateActiveHotTableCellBackgroundColor = ({
    hotInstance,
    session,
    backgroundColor,
}: ActiveHotTableMutationArgs & {
    backgroundColor: string | null;
}): void =>
    updateActiveHotTableSelectionData({
        hotInstance,
        session,
        cellDataUpdateFn: (cellData: CellData) => ({
            ...cellData,
            background: backgroundColor,
        }),
        tableUpdateSource: TableChangeSource.CELL_META_UPDATE_CELL_BACKGROUND,
    });

/**
 * Updates the cell-type formatting for the selected cells in the active HOT instance.
 */
export const updateActiveHotTableCellTypeFormat = ({
    hotInstance,
    session,
    typeFormatChanges,
}: ActiveHotTableMutationArgs & {
    typeFormatChanges: CellTypeObject;
}): void =>
    updateActiveHotTableSelectionData({
        hotInstance,
        session,
        cellDataUpdateFn: (cellData: CellData) => ({
            ...cellData,
            type: { ...cellData.type, ...typeFormatChanges },
        }),
        tableUpdateSource: TableChangeSource.CELL_META_UPDATE_CELL_TYPE_FORMAT,
    });

/**
 * Applies or removes a text style on the selected cells in the active HOT instance.
 */
export const updateActiveHotTableTextStyle = ({
    hotInstance,
    session,
    textStyle,
}: ActiveHotTableMutationArgs & {
    textStyle: string | null;
}): void => {
    const didApplyTextStyle = applyHotTableTextStyle(hotInstance, textStyle);
    if (!didApplyTextStyle) return;

    session.markDirty('CellMeta.updateCellTextStyle');
};
